import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core";
import { environment } from "@env/environment";
import { GameSettingsService } from "../game-settings.service";
import { Resource, ResourceApi } from "@app/shared/sdk";
import {
  GameEnterFreeOption,
  GameMenuEffectOption,
  GameMenuShadowEffectOption,
} from "../shared/types/game-menu-effect-option.model";

@Component({
  selector: "game-menu-art-preview",
  templateUrl: "./game-menu-art-preview.component.html",
  styleUrls: ["./game-menu-art-preview.component.scss"],
})
export class GameMenuArtPreviewComponent implements OnInit {
  @Input() isPrivate = false;
  @Input() inviteType: string | null = null;
  @Input() menuArt1stColor: string | null = "#efeeee";
  @Input() menuArt2ndColor: string | null = "#efeeee";
  @Input() imageScale = 0.5;
  @Input() layerImageScale = 1;
  @Input() toolbarTitle: string | null = null;
  @Output() toolbarTitleChange = new EventEmitter<string>();
  @Input() isSwipeMenu = false;
  @Input() gameId: string | null = null;
  @Input() menuArtUrl: string | null = null;
  @Input() menuArt: string | null = null;
  @Input() layerUrl: string | null = null;
  @Input() layer: string | null = null;
  @Input() optionName: string | null = null;
  @Input() drawDate = "";
  @Input() category: any = null;
  @Input() categories: any[] = [];
  @Output() categoryIdChange = new EventEmitter<string>();

  /** 🆕 structured effect options */
  @Input() titleOpt: GameMenuEffectOption;
  @Input() categoryOpt: GameMenuEffectOption;
  @Input() shadowOpt: GameMenuShadowEffectOption;
  @Input() enterFreeOpt: GameEnterFreeOption;

  // Inline editing state
  isEditingTitle = false;
  editedTitle = "";
  isEditingCategory = false;
  editedCategoryId = "";

  resources: Resource[] = [];
  privateIcon: Resource = new Resource();
  shareIcon: Resource = new Resource();

  s3BucketUrl: string = environment.s3BucketUrl;

  constructor(
    public gameSettingsService: GameSettingsService,
    private resourceApi: ResourceApi
  ) {}

  ngOnInit(): void {
    this.resourceApi
      .find({
        where: { or: [{ key: "privateIcon" }, { key: "shareIcon" }] },
      })
      .subscribe((resources: Resource[]) => {
        this.resources = resources || [];
        if (this.resources.length > 0) {
          const priv = this.resources.find((el) => el.key === "privateIcon");
          if (priv) this.privateIcon = priv;
          const share = this.resources.find((el) => el.key === "shareIcon");
          if (share) this.shareIcon = share;
        }
      });
  }

  getShareLockIconUrl(): string {
    if (!this.resources || this.resources.length === 0) {
      return "";
    }
    const icon = this.isPrivate ? this.privateIcon : this.shareIcon;
    return icon && icon.value ? this.s3BucketUrl + icon.value : "";
  }

  isGameMenuToolbarChecked($event) {
    this.gameSettingsService.isGameMenuToolbar = $event.checked; // Toggle the value of isGameMenuToolbar
  }

  getHeightForOption(optionName: string): string {
    // console.log('optionName...', optionName);
    if (optionName === "large" || optionName === "medium") {
      var width = 270;
      var height = width * 1.48;
      return height + "px";
    }
    return "var(--width)";
  }

  getMenuLayerImage(): string | null {
    if (this.gameId && this.layer) {
      return this.s3BucketUrl + this.layer;
    }
    return this.layerUrl;
  }

  getMenuArtImage(): string | null {
    if (this.gameId && this.menuArt) {
      return this.s3BucketUrl + this.menuArt;
    }
    return this.menuArtUrl;
  }

  isBackgroundBandVisible(): boolean {
    const titleBg = this.titleOpt ? (this.titleOpt.showBackground !== undefined ? !!this.titleOpt.showBackground : this.titleOpt.position === 'top') : false;
    const catBg = this.categoryOpt ? (this.categoryOpt.showBackground !== undefined ? !!this.categoryOpt.showBackground : this.categoryOpt.position === 'top') : false;
    return titleBg || catBg;
  }

  private _lastTitleKey = "";
  private _cachedTitleStyle: { [key: string]: string } = {};

  private _lastCategoryKey = "";
  private _cachedCategoryStyle: { [key: string]: string } = {};

  private _lastShadowKey = "";
  private _cachedShadowStyle = "none";

  /** 🧠 Title style logic (memoized) */
  getTitleStyle(): { [key: string]: string } {
    var key = JSON.stringify([this.titleOpt, this.shadowOpt, this.toolbarTitle]);
    if (key === this._lastTitleKey) {
      return this._cachedTitleStyle;
    }
    this._lastTitleKey = key;

    if (!this.titleOpt || !this.titleOpt.isActive) {
      this._cachedTitleStyle = { display: "none" };
      return this._cachedTitleStyle;
    }

    const titleColor =
      this.titleOpt.color && this.titleOpt.color !== "#c4bfbf"
        ? this.titleOpt.color
        : "#ffffff";

    const shadowColor =
      this.shadowOpt &&
      this.shadowOpt.color &&
      this.shadowOpt.color !== "#c4bfbf"
        ? this.shadowOpt.color
        : "#00ffff";

    const midColor = "#ffff00";

    if (this.titleOpt.efx) {
      this._cachedTitleStyle = {
        color: titleColor,
        "--title-glow-core": titleColor,
        "--title-glow-mid": midColor,
        "--title-glow-mid-pulse": "#ffff88",
        "--title-glow-outer": shadowColor,
        textShadow: `0 0 10px ${titleColor}, 0 0 20px ${midColor}, 0 0 30px ${shadowColor}, 0 0 40px ${shadowColor}, 0 0 60px ${shadowColor}`,
        animation: "floatTitleEfx 4s ease-in-out infinite alternate, pulseGlowTitle 6s ease-in-out infinite",
        fontFamily: "'Orbitron', sans-serif",
        letterSpacing: "0.25rem",
        display: "inline-block",
        transformOrigin: "center",
        willChange: "transform, text-shadow",
        textAlign: "center",
        maxWidth: "100%",
        lineHeight: "1.2",
        fontSize: "1.25rem",
        fontWeight: "700",
      };
      return this._cachedTitleStyle;
    }

    var textShadow =
      this.shadowOpt && this.shadowOpt.isActive
        ? "-1px 1px 10px " + (this.shadowOpt.color || "rgba(0,0,0,0.75)")
        : "none";

    this._cachedTitleStyle = {
      color: this.titleOpt.color || "#000",
      textShadow: textShadow,
      animation: "none",
      fontSize: "1.3rem",
      fontWeight: "bold",
      fontFamily: '"AvenirNextCondensedBold", sans-serif',
      textAlign: "center",
      display: "-webkit-box",
      overflow: "hidden",
      textOverflow: "ellipsis",
      wordBreak: "break-word",
      whiteSpace: "normal", // Override SCSS nowrap
      lineHeight: "1.2",
      maxHeight: "2.4em", // Matches 2 lines * 1.2 line-height
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: "2",
      maxWidth: "100%",
    };
    return this._cachedTitleStyle;
  }

  /** 🧩 Category style logic (memoized) */
  getCategoryStyle(): { [key: string]: string } {
    var key = JSON.stringify([this.categoryOpt, this.titleOpt, this.shadowOpt]);
    if (key === this._lastCategoryKey) {
      return this._cachedCategoryStyle;
    }
    this._lastCategoryKey = key;

    if (!this.categoryOpt || !this.categoryOpt.isActive) {
      this._cachedCategoryStyle = { display: "none" };
      return this._cachedCategoryStyle;
    }

    const categoryColor =
      this.categoryOpt.color && this.categoryOpt.color !== "#c4bfbf"
        ? this.categoryOpt.color
        : "#ff33cc";

    if (this.categoryOpt.efx) {
      this._cachedCategoryStyle = {
        color: categoryColor,
        "--cat-glow-core": "#ffffff",
        "--cat-glow-color": categoryColor,
        "--cat-glow-pulse": "#ff66ee",
        "--cat-glow-deep": "#cc00ff",
        "--cat-glow-deep-pulse": "#dd33ff",
        textShadow: `0 0 12px #fff, 0 0 25px ${categoryColor}, 0 0 40px ${categoryColor}, 0 0 60px #cc00ff`,
        animation: "fadeInQuickCategory 1.5s ease forwards, pulseFocusCategory 3s ease-in-out infinite 1.5s",
        fontFamily: "'Orbitron', sans-serif",
        letterSpacing: "0.22rem",
        display: "inline-block",
        transformOrigin: "center",
        willChange: "opacity, transform, text-shadow",
        textAlign: "center",
        fontSize: "1rem",
        fontWeight: "600",
      };
      return this._cachedCategoryStyle;
    }

    var textShadow =
      this.shadowOpt && this.shadowOpt.isActive
        ? "-1px 1px 10px " + (this.shadowOpt.color || "rgba(0,0,0,0.75)")
        : "none";

    this._cachedCategoryStyle = {
      color: this.categoryOpt.color || "#000",
      textShadow: textShadow,
      animation: "none",
      letterSpacing: "normal",
      fontSize: "14px",
      fontWeight: "500",
    };
    return this._cachedCategoryStyle;
  }

  getShadowStyle(): string {
    var key = JSON.stringify(this.shadowOpt);
    if (key === this._lastShadowKey) {
      return this._cachedShadowStyle;
    }
    this._lastShadowKey = key;

    if (!this.shadowOpt || !this.shadowOpt.isActive) {
      this._cachedShadowStyle = "none";
      return this._cachedShadowStyle;
    }

    // Extract values with fallbacks
    const color = this.shadowOpt.color || "rgba(0, 0, 0, 0.5)";
    const blur =
      this.shadowOpt.blur != null ? `${this.shadowOpt.blur}px` : "4px";

    this._cachedShadowStyle = `1px 1px ${blur} ${color}`;
    return this._cachedShadowStyle;
  }

  /** 📝 Start editing the title */
  startEditingTitle(): void {
    this.isEditingTitle = true;
    this.editedTitle = this.toolbarTitle || "";
  }

  /** 💾 Save the edited title */
  saveTitle(): void {
    if (this.editedTitle.trim()) {
      this.toolbarTitle = this.editedTitle.trim();
      this.toolbarTitleChange.emit(this.toolbarTitle);
    }
    this.isEditingTitle = false;
  }

  /** ❌ Cancel editing */
  cancelEdit(): void {
    this.isEditingTitle = false;
    this.editedTitle = "";
  }

  /** 📝 Start editing the category */
  startEditingCategory(): void {
    this.isEditingCategory = true;
    this.editedCategoryId = this.category ? this.category.id : "";
  }

  /** 💾 Save the edited category */
  saveCategory(): void {
    if (this.editedCategoryId) {
      this.categoryIdChange.emit(this.editedCategoryId);
    }
    this.isEditingCategory = false;
  }

  /** ❌ Cancel category editing */
  cancelCategoryEdit(): void {
    this.isEditingCategory = false;
    this.editedCategoryId = "";
  }
}
